// src/contexts/SchoolContext.js
import { createContext, useContext, useState, useEffect } from "react";
import {
  getSchoolDetails,
  saveSchoolDetails,
  uploadSchoolLogo,
  uploadSchoolHeader,
} from "../api/schoolApi";

const SchoolContext = createContext();

export const SchoolProvider = ({ children }) => {
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch school details on mount
  useEffect(() => {
    const fetchSchoolData = async () => {
      try {
        const data = await getSchoolDetails();
        setSchoolData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchSchoolData();
  }, []);

  const updateSchoolDetails = async (details) => {
    try {
      setLoading(true);
      const updatedData = await saveSchoolDetails(details);
      setSchoolData(updatedData);
      return updatedData;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateLogo = async (logoFile) => {
    try {
      setLoading(true);
      const updatedData = await uploadSchoolLogo(logoFile);
      setSchoolData(updatedData);
      return updatedData;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateHeader = async (headerFile) => {
    try {
      setLoading(true);
      const updatedData = await uploadSchoolHeader(headerFile);
      setSchoolData(updatedData);
      return updatedData;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    schoolData,
    loading,
    error,
    updateSchoolDetails,
    updateLogo,
    updateHeader,
    refreshSchoolData: () => {
      // Re-fetch school data
      setLoading(true);
      getSchoolDetails()
        .then((data) => setSchoolData(data))
        .catch((err) => setError(err.message))
        .finally(() => setLoading(false));
    },
  };

  return (
    <SchoolContext.Provider value={value}>{children}</SchoolContext.Provider>
  );
};

export const useSchool = () => {
  const context = useContext(SchoolContext);
  if (!context) {
    throw new Error("useSchool must be used within a SchoolProvider");
  }
  return context;
};
